import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import printerService from '../../services/printerService';

// ====================================================================
// 1. Funciones de Ayuda (Definiciones de formato)
// ====================================================================

const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num || 0);
};

const formatDate = (dateString) => {
    try {
        if (!dateString) return 'Fecha no disponible';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        return date.toLocaleDateString('es-MX', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch (e) {
        return dateString;
    }
};

// Constantes para áreas táctiles (mínimo 44x44px)
const TOUCH_MIN_SIZE = '44px';

const getAllCategoryDescendants = (categoryId, allCategories) => {
    let ids = [categoryId];
    const children = allCategories.filter(c => c.parent === categoryId);
    for (const child of children) {
        ids = ids.concat(getAllCategoryDescendants(child.id, allCategories));
    }
    return ids;
};

const PuntosVenta = () => {
    const navigate = useNavigate();

    // =====================================
    // 1. ESTADO DE DATOS Y CARGA
    // =====================================
    const [currentShift, setCurrentShift] = useState(null);
    const [currentLocalShift, setCurrentLocalShift] = useState(null);
    const [currentFeriaShift, setCurrentFeriaShift] = useState(null);
    const [checkingShift, setCheckingShift] = useState(true);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processingOrder, setProcessingOrder] = useState(false);
    const [orderResultModal, setOrderResultModal] = useState({ isOpen: false, type: 'success', title: '', message: '' });
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [showOrderDetails, setShowOrderDetails] = useState(false);
    const [editingNoteForItem, setEditingNoteForItem] = useState(null);
    const [noteText, setNoteText] = useState('');

    // 2. ESTADO DEL PUNTO DE VENTA
    const [cart, setCart] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTable, setSelectedTable] = useState('');
    const [discountCode, setDiscountCode] = useState('');
    const [orderDiscountPercentage, setOrderDiscountPercentage] = useState(0);
    const [isEmployeeDiscount, setIsEmployeeDiscount] = useState(false);
    const [appliedDiscount, setAppliedDiscount] = useState(null);

    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [transferReference, setTransferReference] = useState('');

    // Pagos Divididos (Mixtos)
    const [split1Method, setSplit1Method] = useState('efectivo');
    const [split1Amount, setSplit1Amount] = useState('');
    
    const [split2Method, setSplit2Method] = useState('transferencia');
    const [split2Amount, setSplit2Amount] = useState('');

    const [showReviewModal, setShowReviewModal] = useState(false);
    const [checkoutStep, setCheckoutStep] = useState('selection');
    const [isDeliveryNote, setIsDeliveryNote] = useState(false);
    const [customDiscount, setCustomDiscount] = useState('');

    // 3.5 ESTADO DE CALCULADORA DE VUELTO
    const [cashGiven, setCashGiven] = useState(null);
    const [inputCash, setInputCash] = useState('');
    const [exchangeRate, setExchangeRate] = useState(parseFloat(localStorage.getItem('usdExchangeRate')) || 4000);

    // 3. ESTADO DE CLIENTES
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        email: '',
        password: 'Password123!',
        password_confirmation: 'Password123!',
        first_name: '',
        last_name: '',
        phone: '',
        address: '',
        city: '',
        cedula: ''
    });

    // 4. ESTADO PARA TOPPINGS
    const [availableToppings, setAvailableToppings] = useState([]);
    const [showToppingsModal, setShowToppingsModal] = useState(false);
    const [activeToppingsItemIndex, setActiveToppingsItemIndex] = useState(null);

    // 5. ESTADO PARA VARIABLES
    const [showVariablesModal, setShowVariablesModal] = useState(false);
    const [selectedProductForVariables, setSelectedProductForVariables] = useState(null);
    const [currentVariablesSelection, setCurrentVariablesSelection] = useState({});

    // =====================================
    // 4. EFECTOS - CARGA INICIAL DE DATOS Y RESPONSIVIDAD
    // =====================================
    // Inject custom scrollbar styles
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            ::-webkit-scrollbar {
                width: 8px;
                height: 8px;
            }
            ::-webkit-scrollbar-track {
                background: #f1f1f1; 
            }
            ::-webkit-scrollbar-thumb {
                background: var(--border-color); 
                border-radius: 4px;
            }
            ::-webkit-scrollbar-thumb:hover {
                background: var(--border-color); 
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                const [localRes, feriaRes] = await Promise.allSettled([
                    api.get('/api/pos/shifts/current/', {
                        baseURL: process.env.REACT_APP_HELADERIA_SERVICE,
                        params: { is_feria: false }
                    }),
                    api.get('/api/pos/shifts/current/', {
                        baseURL: process.env.REACT_APP_HELADERIA_SERVICE,
                        params: { is_feria: true }
                    })
                ]);
                
                if (isMounted) {
                    const localData = localRes.status === 'fulfilled' ? (localRes.value.data.shift || localRes.value.data) : null;
                    const feriaData = feriaRes.status === 'fulfilled' ? (feriaRes.value.data.shift || feriaRes.value.data) : null;
                    
                    setCurrentLocalShift(localData);
                    setCurrentFeriaShift(feriaData);
                    
                    // The POS is open if AT LEAST ONE shift is open
                    setCurrentShift(localData || feriaData);
                }
            } catch (err) {
                console.warn('Error fetching shifts:', err);
            } finally {
                if (isMounted) {
                    setCheckingShift(false);
                }
            }

            try {
                const productsRes = await api.get('/api/menu/products/', {
                    baseURL: process.env.REACT_APP_HELADERIA_SERVICE
                });

                if (!isMounted) return;

                const loadedProducts = productsRes.data.results || productsRes.data || [];
                loadedProducts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setProducts(loadedProducts);

            } catch (err) {
                console.error('Error cargando productos:', err);
            }

            try {
                const categoriesRes = await api.get('/api/menu/categories/', {
                    baseURL: process.env.REACT_APP_HELADERIA_SERVICE
                });

                if (!isMounted) return;

                const loadedCategories = categoriesRes.data.results || categoriesRes.data || [];
                loadedCategories.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setCategories(loadedCategories);

            } catch (err) {
                console.error('Error cargando categorías:', err);
            }

            try {
                const promosRes = await api.get('/api/menu/combos/?is_promotion=true', {
                    baseURL: process.env.REACT_APP_HELADERIA_SERVICE
                });

                if (!isMounted) return;

                const loadedPromos = promosRes.data.results || promosRes.data || [];
                setPromotions(loadedPromos.filter(p => p.is_active));

            } catch (err) {
                console.error('Error cargando promociones:', err);
            }

            try {
                const tablesRes = await api.get('/api/pos/tables/', {
                    baseURL: process.env.REACT_APP_HELADERIA_SERVICE
                });

                if (!isMounted) return;
                setTables(tablesRes.data.results || tablesRes.data || []);

            } catch (err) {
                console.warn('Mesas no disponibles');
                if (isMounted) {
                    setTables([]);
                }
            }

            try {
                const toppingsRes = await api.get('/api/menu/toppings/', {
                    baseURL: process.env.REACT_APP_HELADERIA_SERVICE
                });
                if (isMounted) {
                    const data = toppingsRes.data.results || toppingsRes.data;
                    const arr = Array.isArray(data) ? data : [];
                    setAvailableToppings(arr.filter(t => t.is_active));
                }
            } catch (err) {
                console.warn('Toppings no disponibles', err);
            }

            if (isMounted) {
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, []);

    // Detectar tamaño de pantalla
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        handleResize(); // Ejecutar al inicio
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // =====================================
    // 5. LÓGICA DEL CARRITO
    // =====================================
    const handleProductClick = (product, isPromo = false, promoQuantity = 1, promoDiscountPct = 0, promoName = '') => {
        if (!isPromo && product.variables && product.variables.length > 0) {
            setSelectedProductForVariables({ product, isPromo, promoQuantity, promoDiscountPct, promoName });
            const initialSelection = {};
            product.variables.forEach(v => {
                initialSelection[v.id] = {}; // No default selection required
            });
            setCurrentVariablesSelection(initialSelection);
            setShowVariablesModal(true);
        } else {
            addToCart(product, isPromo, promoQuantity, promoDiscountPct, promoName);
        }
    };

    const confirmVariablesSelection = () => {
        const { product, isPromo, promoQuantity, promoDiscountPct, promoName } = selectedProductForVariables;
        
        const selectedVariables = [];
        product.variables.forEach(v => {
            const selection = currentVariablesSelection[v.id] || {};
            v.options.forEach(opt => {
                const qty = selection[opt.id] || 0;
                for (let i = 0; i < qty; i++) {
                    selectedVariables.push({
                        variable_id: v.id,
                        variable: v.name,
                        option_id: opt.id,
                        option: opt.name,
                        extra_price: parseFloat(opt.extra_price) || 0
                    });
                }
            });
        });

        addToCart(product, isPromo, promoQuantity, promoDiscountPct, promoName, selectedVariables);
        setShowVariablesModal(false);
        setSelectedProductForVariables(null);
        setCurrentVariablesSelection({});
    };

    const addToCart = useCallback((product, isPromo = false, promoQuantity = 1, promoDiscountPct = 0, promoName = '', selectedVariables = []) => {
        setCart(prevCart => {
            const variablesPriceAdjustment = selectedVariables.reduce((total, v) => total + v.extra_price, 0);
            
            let finalName = isPromo ? promoName : product.name;
            // No concatenamos las variables al nombre para que el ticket y contífico salgan limpios

            const existingItemIndex = prevCart.findIndex(item => 
                item.product_id === product.id && 
                item.isPromo === isPromo &&
                JSON.stringify(item.selectedVariables || []) === JSON.stringify(selectedVariables || []) &&
                !(product.max_toppings > 0)
            );
            if (existingItemIndex >= 0) {
                const newCart = [...prevCart];
                newCart[existingItemIndex] = {
                    ...newCart[existingItemIndex],
                    quantity: newCart[existingItemIndex].quantity + (isPromo ? promoQuantity : 1)
                };
                return newCart;
            } else {
                return [...prevCart, {
                    cartItemId: Date.now() + '-' + Math.random().toString(36).substring(7),
                    product_id: product.id,
                    name: finalName,
                    description: product.description || '',
                    price: parseFloat(product.price) + variablesPriceAdjustment,
                    quantity: isPromo ? promoQuantity : 1,
                    image: isPromo ? product.promoImage : product.image,
                    note: isPromo ? 'Promo aplicada' : '',
                    discount_percentage: isPromo ? promoDiscountPct : 0,
                    isPromo: isPromo,
                    max_toppings: isPromo ? 0 : (product.max_toppings || 0),
                    selectedToppings: [],
                    selectedVariables: selectedVariables,
                    tax_percentage: parseFloat(product.tax_percentage || 0)
                }];
            }
        });
    }, []);

    const handleAddPromo = async (promo) => {
        try {
            const res = await api.get(`/api/menu/combos/${promo.id}/products/`, {
                baseURL: process.env.REACT_APP_HELADERIA_SERVICE
            });
            const promoProducts = res.data;
            if (promoProducts.length > 0) {
                const comboProduct = promoProducts[0];
                const realProduct = comboProduct.product;
                const quantity = comboProduct.quantity;
                
                const originalTotal = realProduct.price * quantity;
                let discountPct = 0;
                if (originalTotal > 0 && promo.price < originalTotal) {
                    discountPct = ((originalTotal - promo.price) / originalTotal) * 100;
                }
                
                // Agregamos imagen de la promo al producto temporalmente
                realProduct.promoImage = promo.image;
                
                addToCart(realProduct, true, quantity, discountPct, promo.name);
            } else {
                alert("Esta promoción no tiene productos asociados.");
            }
        } catch(e) {
            console.error("Error al cargar la promo", e);
        }
    };

    const removeFromCart = useCallback((itemToRemove) => {
        setCart(prevCart => prevCart.filter(item => 
            item.cartItemId !== itemToRemove.cartItemId
        ));
    }, []);

    const updateQuantity = useCallback((itemToUpdate, delta) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === itemToUpdate.cartItemId) {
                    const newQuantity = Math.max(1, item.quantity + delta);
                    return { ...item, quantity: newQuantity };
                }
                return item;
            });
        });
    }, []);

    const updateItemDiscount = useCallback((itemToUpdate, percentage) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === itemToUpdate.cartItemId) {
                    return { ...item, discount_percentage: Math.max(0, Math.min(100, percentage)) };
                }
                return item;
            });
        });
    }, []);

    const handleAddNote = (itemToNote) => {
        const item = cart.find(i => i.cartItemId === itemToNote.cartItemId);
        setEditingNoteForItem(itemToNote.cartItemId);
        setNoteText(item?.note || '');
    };

    const saveNote = () => {
        if (!editingNoteForItem) return;

        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === editingNoteForItem) {
                    return { ...item, note: noteText.trim() };
                }
                return item;
            });
        });

        setEditingNoteForItem(null);
        setNoteText('');
    };

    const cancelNote = () => {
        setEditingNoteForItem(null);
        setNoteText('');
    };

    // =====================================
    // 6. CÁLCULOS DE PRECIOS
    // =====================================
    const calculateSubtotal = useMemo(() => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    }, [cart]);

    const calculateItemDiscounts = useMemo(() => {
        return cart.reduce((total, item) => {
            const percentage = parseFloat(item.discount_percentage) || 0;
            return total + (item.price * (percentage / 100) * item.quantity);
        }, 0);
    }, [cart]);

    const calculateOrderDiscount = useMemo(() => {
        const subtotalAfterItemDiscounts = calculateSubtotal - calculateItemDiscounts;
        const percentage = parseFloat(orderDiscountPercentage) || 0;
        return subtotalAfterItemDiscounts * (percentage / 100);
    }, [calculateSubtotal, calculateItemDiscounts, orderDiscountPercentage]);

    const calculateDiscountAmount = useMemo(() => {
        // También mantenemos appliedDiscount por si acaso
        let oldDiscount = 0;
        if (appliedDiscount) {
            const subtotal = calculateSubtotal - calculateItemDiscounts;
            if (appliedDiscount.discount_type === 'percentage') {
                oldDiscount = subtotal * (parseFloat(appliedDiscount.discount_value) / 100);
            } else if (appliedDiscount.discount_type === 'fixed_amount') {
                oldDiscount = Math.min(parseFloat(appliedDiscount.discount_value), subtotal);
            }
        }
        return calculateItemDiscounts + calculateOrderDiscount + oldDiscount;
    }, [calculateItemDiscounts, calculateOrderDiscount, appliedDiscount, calculateSubtotal]);

    const calculateTotal = useMemo(() => {
        const subtotal = calculateSubtotal;
        const discount = calculateDiscountAmount;
        return (subtotal - discount);
    }, [calculateSubtotal, calculateDiscountAmount]);

    // =====================================
    // 7. LÓGICA DE DESCUENTOS
    // =====================================
    const handleApplyDiscount = async () => {
        if (!discountCode) return;
        try {
            const response = await api.post('/api/pos/discounts/validate/', { code: discountCode }, {
                baseURL: process.env.REACT_APP_HELADERIA_SERVICE
            });
            if (response.data.valid) {
                setAppliedDiscount(response.data.discount);
                alert('Descuento aplicado correctamente');
            } else {
                alert(response.data.message || 'Código inválido');
                setAppliedDiscount(null);
            }
        } catch (err) {
            console.error('Error validating discount:', err);
            alert('Error al validar descuento');
            setAppliedDiscount(null);
        }
    };

    // =====================================
    // 8. LÓGICA DE CLIENTES
    // =====================================
    const searchCustomers = async (query) => {
        setCustomerSearch(query);
        if (query.length < 3) {
            setCustomers([]);
            return;
        }
        try {
            const response = await api.post('/api/customers/admin/search/', { query }, {
                baseURL: process.env.REACT_APP_HELADERIA_SERVICE
            });
            setCustomers(response.data.data.customers || []);
        } catch (err) {
            console.error('Error searching customers:', err);
        }
    };

    const handleCreateCustomer = async (e) => {
        e.preventDefault();
        try {
            const customerData = {
                ...newCustomer,
                cedula: newCustomer.cedula || null
            };

            const response = await api.post('/api/customers/register/', customerData, {
                baseURL: process.env.REACT_APP_HELADERIA_SERVICE
            });
            alert('Cliente creado exitosamente');
            setShowCustomerModal(false);
            setSelectedCustomer(response.data.data.customer);
            setCustomerSearch(`${response.data.data.customer.first_name} ${response.data.data.customer.last_name}`);
            setCustomers([]);
            setNewCustomer({
                email: '',
                password: 'Password123!',
                password_confirmation: 'Password123!',
                first_name: '',
                last_name: '',
                phone: '',
                address: '',
                city: '',
                cedula: ''
            });
        } catch (err) {
            console.error('Error creating customer:', err);
            const errorData = err.response?.data;
            let errorMessage = 'Error al crear cliente';

            if (errorData?.errors) {
                errorMessage += ':\n' + Object.entries(errorData.errors)
                    .map(([key, val]) => `- ${key}: ${val}`)
                    .join('\n');
            } else if (errorData?.message) {
                errorMessage += ': ' + errorData.message;
            } else {
                errorMessage += ': ' + err.message;
            }

            alert(errorMessage);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewCustomer(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // =====================================
    // 9. LÓGICA DE PROCESAMIENTO PRINCIPAL
    // =====================================

    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
            const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
            const isAvailable = product.is_active && product.is_available;

            return matchesCategory && matchesSearch && isAvailable;
        });
    }, [products, selectedCategory, searchTerm]);

    // Validation for split payments
    const isPaymentValid = useMemo(() => {
        if (paymentMethod !== 'dividido') return true;
        const currentExchangeRate = parseFloat(exchangeRate) || 4000;
        
        const getUsdAmount = (curr, amount) => curr === 'USD' ? parseFloat(amount || 0) : parseFloat(amount || 0) / currentExchangeRate;
        
        const split1Usd = parseFloat(String(split1Amount || '').replace(',', '.')) || 0;
        const split2Usd = parseFloat(String(split2Amount || '').replace(',', '.')) || 0;
        
        // Allowed tolerance of 0.02 USD to handle rounding differences
        return (split1Usd + split2Usd) >= (calculateTotal - 0.02);
    }, [paymentMethod, split1Amount, split2Amount, calculateTotal, exchangeRate]);

    // 🖨️ FUNCIÓN PRINCIPAL CON IMPRESIÓN
    const finalPlaceOrder = async (isFeria = false) => {
        if (cart.length === 0) return;

        setProcessingOrder(true);
        setShowReviewModal(false);

        let orderType = 'dine_in';
        let tableNumber = selectedTable;
        const DEFAULT_TABLE_NAME = 'GENERICA';

        if (selectedTable === 'takeout') {
            orderType = 'takeout';
            tableNumber = '';
        } else if (!selectedTable || selectedTable === 'Seleccionar mesa...') {
            orderType = 'dine_in';
            tableNumber = DEFAULT_TABLE_NAME;
        } else {
            orderType = 'dine_in';
            tableNumber = selectedTable;
        }

        // Preparar notas con información de pago
        let orderNotes = '';
        if (isEmployeeDiscount) {
            orderNotes += '[VENTA_EMPLEADO]';
        }
        if (cashGiven && paymentMethod !== 'dividido') {
            const displayTotal = calculateTotal;
            const change = cashGiven - displayTotal;
            if (orderNotes) orderNotes += ' | ';
            const currSymbol = '$';
            orderNotes += `Pago con: ${currSymbol} ${cashGiven} - Cambio: ${currSymbol} ${change}`;
        }

        // Modificado para incluir notas en los items y porcentajes de descuento
        const orderPayload = {
            order_type: orderType,
            table_number: tableNumber,
            notes: orderNotes, // Nueva nota general
            discount_percentage: Number((parseFloat(orderDiscountPercentage) || 0).toFixed(2)),
            payment_method: paymentMethod,
            payment_reference: paymentMethod === 'efectivo' || paymentMethod === 'tarjeta' ? '' : transferReference,
            items: cart.map(item => {
                let finalNote = item.note || '';
                if (item.selectedToppings && item.selectedToppings.length > 0) {
                    const toppingsText = `Toppings: ${item.selectedToppings.join(', ')}`;
                    finalNote = finalNote ? `${finalNote} | ${toppingsText}` : toppingsText;
                }
                return {
                    product_id: item.product_id,
                    quantity: item.quantity,
                    notes: finalNote, 
                    discount_percentage: Number((parseFloat(item.discount_percentage) || 0).toFixed(2)),
                    selected_variables: item.selectedVariables || []
                };
            }),
            discount_code: appliedDiscount ? appliedDiscount.code : null,
            customer_id: selectedCustomer ? selectedCustomer.id : null,
            skip_print: true, // Evitar que el backend imprima doble
            is_feria: isFeria,
            is_delivery_note: isDeliveryNote
        };
        
        if (paymentMethod === 'dividido') {
            // Ensure parse values correctly
            const split1Usd = parseFloat(String(split1Amount || '').replace(',', '.')) || 0;
            const split2Usd = parseFloat(String(split2Amount || '').replace(',', '.')) || 0;
            const split3Usd = parseFloat(String(transferReference || '').replace(',', '.')) || 0;
            
            const totalEnteredUsd = split1Usd + split2Usd + split3Usd;
            
            // Allow a tiny tolerance for floating point math
            if (totalEnteredUsd < calculateTotal - 0.01) {
                alert(`El monto ingresado en el pago dividido ($${totalEnteredUsd.toFixed(2)} USD) es menor al Total Final ($${calculateTotal.toFixed(2)} USD). Por favor, verifica los montos ingresados.`);
                setProcessingOrder(false);
                return;
            }
            
            if (split1Usd > 0) orderPayload.cash_amount = split1Usd;
            if (split2Usd > 0) orderPayload.card_amount = split2Usd;
            if (split3Usd > 0) orderPayload.transfer_amount = split3Usd;
        } else if (paymentMethod === 'efectivo') {
            orderPayload.cash_amount = Number(calculateTotal.toFixed(2));
            orderPayload.transfer_amount = 0;
        } else {
            orderPayload.cash_amount = 0;
            orderPayload.transfer_amount = Number(calculateTotal.toFixed(2));
        }

        try {
            // 1. CREAR LA ORDEN
            const orderResponse = await api.post('/api/orders/orders/', orderPayload, {
                baseURL: process.env.REACT_APP_HELADERIA_SERVICE
            });

            const createdOrder = orderResponse.data;

            // 2. PREPARAR DATOS PARA EL TICKET (incluyendo notas)
            
            let receipt_subtotal = 0;
            let receipt_tax = 0;
            let receipt_total = 0;
            let receipt_discount = 0;

            const receiptItems = cart.map(item => {
                let receiptNote = item.note || '';
                if (item.selectedToppings && item.selectedToppings.length > 0) {
                    const toppingsText = `Toppings: ${item.selectedToppings.join(', ')}`;
                    receiptNote = receiptNote ? `${receiptNote} | ${toppingsText}` : toppingsText;
                }
                if (item.selectedVariables && item.selectedVariables.length > 0) {
                    const variablesText = item.selectedVariables.map(v => `${v.variable}: ${v.option}`).join(', ');
                    receiptNote = receiptNote ? `${receiptNote} | ${variablesText}` : variablesText;
                }
                
                const finalPrice = parseFloat(item.price);
                const discountPctItem = parseFloat(item.discount_percentage) || 0;
                const totalFinal = finalPrice * item.quantity * (1 - discountPctItem / 100);
                
                const taxPct = parseFloat(item.tax_percentage) || 0;
                const factor = 1 + (taxPct / 100);
                
                const baseItem = totalFinal / factor;
                const taxItem = totalFinal - baseItem;
                
                receipt_subtotal += baseItem;
                receipt_tax += taxItem;
                receipt_total += totalFinal;
                // El descuento del item ya se restó en totalFinal, si quieres mostrar el descuento bruto:
                receipt_discount += (finalPrice * item.quantity) - totalFinal;

                return {
                    name: item.name,
                    description: item.description || '',
                    quantity: item.quantity,
                    price: finalPrice,
                    discount_percentage: Number(discountPctItem.toFixed(2)),
                    total: Number(totalFinal.toFixed(2)),
                    note: receiptNote
                };
            });
            
            // Restar descuento general (de orden) si existe
            if (calculateDiscountAmount > receipt_discount) {
                const extra_discount = calculateDiscountAmount - receipt_discount;
                // Distribuirlo (simplificado) reduciendo el subtotal y tax proporcionalmente
                // En la práctica, el POS de KEKALA solo resta el descuento del total final.
                receipt_total -= extra_discount;
                receipt_discount += extra_discount;
                // Se asume que el descuento extra reduce la base y el iva proporcionalmente
                // pero por simplicidad de impresión ajustamos solo el subtotal/total
            }

            const receiptData = {
                order_number: createdOrder.order_number || createdOrder.id,
                customer_name: selectedCustomer
                    ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`
                    : 'CONSUMIDOR FINAL',
                cashier_name: currentShift?.user_name || 'CAJA',
                table_number: selectedTable === 'takeout' ? 'PARA LLEVAR' : (selectedTable || 'MESA GENÉRICA'),
                items: receiptItems,
                subtotal: Number(parseFloat(calculateSubtotal - calculateDiscountAmount - receipt_tax).toFixed(2)),
                discount: Number(parseFloat(calculateDiscountAmount).toFixed(2)),
                tax: Number(parseFloat(receipt_tax).toFixed(2)),
                total: Number(parseFloat(calculateTotal).toFixed(2)),
                payment_method: paymentMethod,
                payment_reference: transferReference,
                printed_at: new Date().toISOString() // Hora del cliente para el ticket
            };

            // 3. ENVIAR A IMPRIMIR (esto abre la caja automáticamente)
            try {
                const printResult = await printerService.printReceipt(receiptData);
                console.log('✅ Ticket enviado a impresión:', printResult);

                let modalType = 'success';
                let modalTitle = '¡Orden y Factura Creadas!';
                let modalMessage = `Orden: ${createdOrder.order_number || createdOrder.id}\nTicket: ${printResult.job_number}\n\nEl ticket se está imprimiendo y la caja se abrirá.`;
                
                if (createdOrder.warning) {
                    modalType = 'warning';
                    modalTitle = 'Orden Creada con Advertencia';
                    modalMessage = `${createdOrder.warning}\n\nOrden: ${createdOrder.order_number || createdOrder.id}\nTicket: ${printResult.job_number}\n\nEl ticket se está imprimiendo y la caja se abrirá.`;
                }

                setOrderResultModal({
                    isOpen: true,
                    type: modalType,
                    title: modalTitle,
                    message: modalMessage
                });
            } catch (printError) {
                console.error('⚠️ Error al imprimir:', printError);

                let modalType = 'warning';
                let modalTitle = 'Orden Creada (Fallo al imprimir)';
                let modalMessage = `Orden: ${createdOrder.order_number || createdOrder.id}\n\nError: ${printError.response?.data?.error || 'Error de conexión'}\nVerifica que el agente de Windows esté ejecutándose.`;

                if (createdOrder.warning) {
                    modalTitle = 'Orden Creada con Advertencias';
                    modalMessage = `${createdOrder.warning}\n\n${modalMessage}`;
                }

                setOrderResultModal({
                    isOpen: true,
                    type: modalType,
                    title: modalTitle,
                    message: modalMessage
                });
            }

            // 4. LIMPIAR EL CARRITO Y ESTADOS DE PAGO
            setCart([]);
            setAppliedDiscount(null);
            setDiscountCode('');
            setIsEmployeeDiscount(false);
            setOrderDiscountPercentage(0);
            setCustomDiscount('');
            setSelectedTable('');
            setSelectedCustomer(null);
            setCustomerSearch('');
            setCashGiven(null); // Resetear calculadora
            setInputCash('');
            setPaymentMethod('efectivo');
            setTransferReference('');
            setSplit1Amount('');
            setSplit2Amount('');
            setSplit1Method('efectivo');
            setSplit2Method('transferencia');
            setIsDeliveryNote(false);
            setCheckoutStep('selection');
        } catch (err) {
            console.error('❌ Error al procesar la orden:', err);
            let errorMsg = 'Error al procesar la orden';
            if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            } else if (err.response?.data) {
                errorMsg = JSON.stringify(err.response.data);
            }
            setOrderResultModal({
                isOpen: true,
                type: 'error',
                title: 'Error de Facturación (Contífico)',
                message: errorMsg
            });
            // Importante: No limpiamos el carrito ni cerramos el modal de pago
        } finally {
            setProcessingOrder(false);
        }
    };

    // 🔓 FUNCIÓN PARA ABRIR CAJA MANUALMENTE
    const handleOpenCashDrawer = async () => {
        try {
            await printerService.openCashDrawer();
            alert('✅ Caja abierta');
        } catch (error) {
            alert('❌ Error al abrir caja. Verifica que el agente esté ejecutándose.');
        }
    };

    const openOrderConfirmationModal = () => {
        if (cart.length === 0) {
            alert("El carrito está vacío.");
            return;
        }
        setCheckoutStep('document_type');
        setShowReviewModal(true);
    };

    // =====================================
    // 10. COMPONENTES DE RENDERIZADO
    // =====================================

    const renderReviewDetails = () => {
        const goBack = () => {
            setCheckoutStep('selection');
            setPaymentMethod('efectivo');
            setCashGiven(null);
            setInputCash('');
        };

        const confirmOrder = () => {
            finalPlaceOrder(false);
        };

        const modalHeaderStyle = {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb'
        };
        const closeBtn = (onClick) => (
            <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#6b7280', padding: '0.25rem', lineHeight: 1 }}>
                ×
            </button>
        );

        if (checkoutStep === 'document_type') {
            return (
                <div>
                    <div style={modalHeaderStyle}>
                        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Tipo de Documento</span>
                        {closeBtn(() => setShowReviewModal(false))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '1.5rem' }}>
                        <button onClick={() => { setIsDeliveryNote(false); setCheckoutStep('selection'); }}
                            style={{ padding: '2rem 1rem', backgroundColor: '#fff', border: '2px solid #3b82f6', borderRadius: '12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)' }}>
                            <span style={{ fontSize: '2.5rem' }}>🧾</span>
                            <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e3a8a' }}>Factura (SRI)</span>
                        </button>
                        <button onClick={() => { setIsDeliveryNote(true); setCheckoutStep('selection'); }}
                            style={{ padding: '2rem 1rem', backgroundColor: '#fff', border: '2px solid #f59e0b', borderRadius: '12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.1)' }}>
                            <span style={{ fontSize: '2.5rem' }}>📄</span>
                            <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#b45309' }}>Nota de Entrega</span>
                            <span style={{ fontSize: '0.8rem', color: '#d97706', textAlign: 'center' }}>(Control Interno)</span>
                        </button>
                    </div>
                </div>
            );
        }

        if (checkoutStep === 'dividido') {
            const getUsdAmount = (amount) => parseFloat(String(amount || '').replace(',', '.')) || 0;

            const split1Usd = getUsdAmount(split1Amount);       // Efectivo
            const transferUsd = getUsdAmount(transferReference); // Transferencia
            const split2Usd = getUsdAmount(split2Amount);       // Tarjeta
            const totalEnteredUsd = split1Usd + transferUsd + split2Usd;
            
            const remainingToPay = Math.max(0, calculateTotal - totalEnteredUsd);
            const hasEnough = totalEnteredUsd >= calculateTotal - 0.01;

            const inputStyle = { width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '1rem', boxSizing: 'border-box' };
            const labelStyle = { display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' };

            return (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <button onClick={() => setCheckoutStep('selection')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6b7280' }}>
                                ← Atrás
                            </button>
                            <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Pago Mixto</span>
                        </div>
                        {closeBtn(() => setShowReviewModal(false))}
                    </div>

                    <div style={{ padding: '1.25rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.25rem', fontSize: '1.1rem', fontWeight: '600' }}>
                            <span>Total a Pagar:</span>
                            <span style={{ color: 'var(--primary-color)' }}>{formatCurrency(calculateTotal)}</span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {/* Efectivo */}
                            <div>
                                <label style={labelStyle}>💵 Efectivo (USD)</label>
                                <input
                                    type="number"
                                    value={split1Amount}
                                    onChange={(e) => setSplit1Amount(e.target.value)}
                                    placeholder="0.00"
                                    style={inputStyle}
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            {/* Transferencia */}
                            <div>
                                <label style={labelStyle}>🏦 Transferencia (USD)</label>
                                <input
                                    type="number"
                                    value={transferReference}
                                    onChange={(e) => setTransferReference(e.target.value)}
                                    placeholder="0.00"
                                    style={inputStyle}
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            {/* Tarjeta */}
                            <div>
                                <label style={labelStyle}>💳 Tarjeta (USD)</label>
                                <input
                                    type="number"
                                    value={split2Amount}
                                    onChange={(e) => setSplit2Amount(e.target.value)}
                                    placeholder="0.00"
                                    style={inputStyle}
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        {/* Resumen de montos ingresados */}
                        {totalEnteredUsd > 0 && (
                            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                                {split1Usd > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Efectivo:</span><span>{formatCurrency(split1Usd)}</span></div>}
                                {transferUsd > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Transferencia:</span><span>{formatCurrency(transferUsd)}</span></div>}
                                {split2Usd > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tarjeta:</span><span>{formatCurrency(split2Usd)}</span></div>}
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', color: '#111827', borderTop: '1px solid #e5e7eb', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                                    <span>Total ingresado:</span><span>{formatCurrency(totalEnteredUsd)}</span>
                                </div>
                            </div>
                        )}

                        <div style={{ 
                            marginTop: '1rem', 
                            padding: '1rem', 
                            backgroundColor: hasEnough ? '#dcfce7' : '#fee2e2', 
                            borderRadius: '10px', 
                            textAlign: 'center',
                            marginBottom: '1rem'
                        }}>
                            <span style={{ 
                                display: 'block', 
                                color: hasEnough ? '#166534' : '#991b1b', 
                                fontWeight: '700', 
                                fontSize: '1.125rem' 
                            }}>
                                {hasEnough ? '✅ Pago completo' : `Faltante: ${formatCurrency(remainingToPay)}`}
                            </span>
                        </div>

                        {/* Botón Principal */}
                        <button
                            style={{
                                width: '100%',
                                padding: '1rem',
                                backgroundColor: (!hasEnough || processingOrder) ? '#d1d5db' : 'var(--primary-color)',
                                border: 'none',
                                borderRadius: '10px',
                                color: '#ffffff',
                                fontSize: '1.125rem',
                                fontWeight: '700',
                                cursor: (!hasEnough || processingOrder) ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: (!hasEnough || processingOrder) ? 'none' : '0 4px 12px rgba(100, 16, 14, 0.3)',
                                marginBottom: '0.75rem',
                                minHeight: TOUCH_MIN_SIZE
                            }}
                            onClick={confirmOrder}
                            disabled={!hasEnough || processingOrder}
                        >
                            {processingOrder ? 'Procesando pedido...' : 'Cobrar e Imprimir'}
                        </button>
                    </div>
                </div>
            );
        }

        if (checkoutStep === 'cliente') {
            return (
                <div>
                    {/* Header */}
                    <div style={modalHeaderStyle}>
                        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Datos de Facturación</span>
                        {closeBtn(() => setCheckoutStep('selection'))}
                    </div>

                    <div style={{ padding: '1rem 1.25rem' }}>
                        {/* Buscador */}
                        <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre, cédula o teléfono..."
                                    style={{ flex: 1, padding: '0.75rem', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                                    value={customerSearch}
                                    onChange={(e) => searchCustomers(e.target.value)}
                                />
                                <button
                                    style={{ width: '42px', height: '42px', backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1.4rem', fontWeight: 'bold', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={() => setShowCustomerModal(true)}
                                    title="Crear nuevo cliente"
                                >+</button>
                            </div>
                            {customers.length > 0 && (
                                <div style={{ position: 'absolute', zIndex: 10, width: '100%', backgroundColor: '#fff', border: '1.5px solid #d1d5db', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                    {customers.map(c => (
                                        <div key={c.id}
                                            style={{ padding: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                                            onClick={() => {
                                                setSelectedCustomer(c);
                                                setCustomerSearch(`${c.first_name} ${c.last_name}`);
                                                setCustomers([]);
                                                setCheckoutStep('selection');
                                            }}>
                                            <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#111827' }}>{c.first_name} {c.last_name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{c.cedula || c.email}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {selectedCustomer && (
                            <div style={{ padding: '0.75rem', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ fontWeight: '700', color: '#166534', fontSize: '0.9rem' }}>✓ {selectedCustomer.first_name} {selectedCustomer.last_name}</div>
                                    <div style={{ fontSize: '0.8rem', color: '#16a34a' }}>{selectedCustomer.cedula}</div>
                                </div>
                                <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 'bold', fontSize: '1.1rem' }}>×</button>
                            </div>
                        )}

                        <button style={{ width: '100%', padding: '0.75rem', backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}
                            onClick={() => setCheckoutStep('selection')}>
                            Confirmar y Volver
                        </button>
                    </div>
                </div>
            );
        }

        if (checkoutStep === 'descuento') {
            return (
                <div>
                    <div style={modalHeaderStyle}>
                        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Aplicar Descuento</span>
                        {closeBtn(() => setCheckoutStep('selection'))}
                    </div>
                    <div style={{ padding: '1rem 1.25rem' }}>
                        <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '0.75rem', fontSize: '0.9rem' }}>Escribe el porcentaje de descuento a aplicar</p>
                        <div style={{ position: 'relative', marginBottom: '1rem' }}>
                            <input
                                type="number"
                                placeholder="0"
                                style={{ width: '100%', padding: '1rem 2.5rem 1rem 1rem', border: '2px solid #d1d5db', borderRadius: '8px', fontSize: '1.8rem', textAlign: 'center', fontWeight: '700', boxSizing: 'border-box' }}
                                value={customDiscount}
                                autoFocus
                                onChange={(e) => {
                                    let val = e.target.value;
                                    if (val > 100) val = 100;
                                    if (val < 0) val = 0;
                                    setCustomDiscount(val);
                                }}
                            />
                            <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', fontSize: '1.5rem', color: '#9ca3af', fontWeight: '700' }}>%</span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            {[5, 10, 15, 20].map(p => (
                                <button key={p} onClick={() => setCustomDiscount(p.toString())}
                                    style={{ flex: 1, padding: '0.6rem', backgroundColor: customDiscount == p ? 'var(--primary-color)' : '#f3f4f6', color: customDiscount == p ? '#fff' : '#374151', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}>
                                    {p}%
                                </button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button style={{ flex: 1, padding: '0.85rem', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}
                                onClick={() => { setOrderDiscountPercentage(0); setCustomDiscount(''); setCheckoutStep('selection'); }}>Quitar</button>
                            <button style={{ flex: 2, padding: '0.85rem', backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}
                                onClick={() => { setOrderDiscountPercentage(customDiscount ? parseFloat(customDiscount) : 0); setCheckoutStep('selection'); }}>
                                Aplicar Descuento
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        if (checkoutStep === 'efectivo') {
            const change = cashGiven !== null ? cashGiven - calculateTotal : null;
            const isInsufficient = cashGiven !== null && cashGiven < calculateTotal;
            return (
                <div>
                    {/* Header */}
                    <div style={modalHeaderStyle}>
                        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Pago en Efectivo</span>
                        {closeBtn(goBack)}
                    </div>
                    <div style={{ padding: '0.75rem 1.25rem' }}>
                        <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>Ingrese el monto recibido del cliente</p>
                        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Total a cobrar</div>
                            <div style={{ fontSize: '2rem', fontWeight: '900', color: 'var(--primary-color)' }}>{formatCurrency(calculateTotal)}</div>
                        </div>

                        {/* Monto recibido display */}
                        <div style={{ border: '2px solid #d1d5db', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#fafafa' }}>
                            <span style={{ fontSize: '1.1rem', color: '#6b7280', fontWeight: '700' }}>$</span>
                            <input
                                type="number"
                                value={inputCash}
                                onChange={(e) => { const val = e.target.value; setInputCash(val); setCashGiven(val ? parseFloat(val) : null); }}
                                placeholder="0"
                                style={{ flex: 1, border: 'none', background: 'none', fontSize: '1.4rem', fontWeight: '700', textAlign: 'center', outline: 'none' }}
                            />
                        </div>

                        {/* Botones de billetes rápidos */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            {[1, 5, 10, 20].map(bill => (
                                <button key={bill} onClick={() => { const newVal = (cashGiven || 0) + bill; setCashGiven(newVal); setInputCash(newVal.toString()); }}
                                    style={{ padding: '0.65rem', backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe', borderRadius: '8px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' }}>
                                    +${bill}
                                </button>
                            ))}
                        </div>

                        {/* Botón borrar */}
                        {cashGiven !== null && (
                            <button onClick={() => { setCashGiven(null); setInputCash(''); }}
                                style={{ width: '100%', padding: '0.5rem', backgroundColor: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                                Borrar
                            </button>
                        )}

                        {/* Resultado de vuelto */}
                        {change !== null && (
                            <div style={{ padding: '0.75rem', backgroundColor: isInsufficient ? '#fef2f2' : '#f0fdf4', border: '1px solid ' + (isInsufficient ? '#fca5a5' : '#86efac'), borderRadius: '8px', textAlign: 'center', marginBottom: '0.75rem' }}>
                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Vuelto</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: '900', color: isInsufficient ? '#ef4444' : '#16a34a' }}>
                                    {isInsufficient ? '⚠ Insuficiente' : formatCurrency(change)}
                                </div>
                            </div>
                        )}

                        {/* Botones de acción */}
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button style={{ flex: 1, padding: '0.85rem', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#374151', cursor: 'pointer' }} onClick={goBack}>Regresar</button>
                            <button style={{ flex: 2, padding: '0.85rem', backgroundColor: isInsufficient ? '#d1d5db' : 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '1rem', cursor: isInsufficient ? 'not-allowed' : 'pointer' }}
                                onClick={confirmOrder} disabled={isInsufficient}>
                                Confirmar Cobro
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        if (checkoutStep === 'tarjeta' || checkoutStep === 'transferencia') {
            const isTarjeta = checkoutStep === 'tarjeta';
            const label = isTarjeta ? 'tarjeta' : 'transferencia';
            return (
                <div>
                    <div style={modalHeaderStyle}>
                        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>
                            Pago con {isTarjeta ? 'Tarjeta' : 'Transferencia'}
                        </span>
                        {closeBtn(goBack)}
                    </div>
                    <div style={{ padding: '1.25rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>{isTarjeta ? '💳' : '🔄'}</div>
                        <p style={{ color: '#374151', fontSize: '1rem', marginBottom: '1rem' }}>
                            ¿Está seguro de cobrar el pago con <strong>{label}</strong>?
                        </p>
                        <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '10px', marginBottom: '1.25rem' }}>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Total a cobrar</div>
                            <div style={{ fontSize: '2.2rem', fontWeight: '900', color: 'var(--primary-color)' }}>{formatCurrency(calculateTotal)}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button style={{ flex: 1, padding: '0.85rem', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#374151', cursor: 'pointer' }} onClick={goBack}>Regresar</button>
                            <button style={{ flex: 2, padding: '0.85rem', backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}
                                onClick={confirmOrder}>
                                Sí, Cobrar
                            </button>
                        </div>
                    </div>
                </div>
            );
        }


        // checkoutStep === 'selection' (Default)
        return (
            <div>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Método de Pago</span>
                    <button onClick={() => setShowReviewModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#6b7280', lineHeight: 1, padding: '0.25rem' }}>×</button>
                </div>

                {/* Total + descuento badge */}
                <div style={{ padding: '0.75rem 1.25rem 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.95rem', color: '#374151' }}>
                        Total: <strong>{formatCurrency(calculateTotal)}</strong>
                    </span>
                    {orderDiscountPercentage > 0 && (
                        <span style={{ backgroundColor: '#f59e0b', color: '#fff', borderRadius: '20px', padding: '0.2rem 0.75rem', fontSize: '0.8rem', fontWeight: 'bold' }}>
                            Descuento {orderDiscountPercentage}%
                        </span>
                    )}
                </div>

                {/* Botones Con Datos y Descuento */}
                <div style={{ display: 'flex', gap: '0.5rem', padding: '0 1.25rem 0.75rem' }}>
                    <button style={{ flex: 1, padding: '0.55rem', backgroundColor: '#f3f4f6', border: '1.5px solid #d1d5db', borderRadius: '8px', fontWeight: '600', color: '#374151', fontSize: '0.85rem', cursor: 'pointer' }}
                        onClick={() => setCheckoutStep('cliente')}>
                        👤 {selectedCustomer ? selectedCustomer.first_name : 'Con Datos'}
                    </button>
                    <button style={{ flex: 1, padding: '0.55rem', backgroundColor: orderDiscountPercentage > 0 ? '#e0e7ff' : '#f3f4f6', border: '1.5px solid ' + (orderDiscountPercentage > 0 ? '#6366f1' : '#d1d5db'), borderRadius: '8px', fontWeight: '600', color: orderDiscountPercentage > 0 ? '#4f46e5' : '#374151', fontSize: '0.85rem', cursor: 'pointer' }}
                        onClick={() => { setCustomDiscount(orderDiscountPercentage); setCheckoutStep('descuento'); }}>
                        % Descuento
                    </button>
                </div>

                {/* Grid 2x2 de métodos de pago */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', padding: '0 1.25rem 1.25rem' }}>
                    <button onClick={() => { setPaymentMethod('efectivo'); setCheckoutStep('efectivo'); }}
                        style={{ padding: '1.25rem 0.5rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.12)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#10b981" viewBox="0 0 256 256">
                            <path d="M240,88V184a16,16,0,0,1-16,16H32a16,16,0,0,1-16-16V88A16,16,0,0,1,32,72H224A16,16,0,0,1,240,88Zm-16,0H32V184H224ZM128,104a24,24,0,1,0,24,24A24,24,0,0,0,128,104Zm0,32a8,8,0,1,1,8-8A8,8,0,0,1,128,136Zm88-32a8,8,0,0,0-8-8H192a8,8,0,0,0,0,16h16A8,8,0,0,0,216,104Zm-16,64H192a8,8,0,0,0,0,16h8a8,8,0,0,0,0-16ZM64,104a8,8,0,0,0,8-8H56a8,8,0,0,0,0,16h8A8,8,0,0,0,64,104Zm0,64a8,8,0,0,0-8,8h16a8,8,0,0,0,0-16H56A8,8,0,0,0,64,168Z"/>
                        </svg>
                        <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#111827' }}>Efectivo</span>
                    </button>

                    <button onClick={() => { setPaymentMethod('tarjeta'); setCheckoutStep('tarjeta'); }}
                        style={{ padding: '1.25rem 0.5rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.12)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#3b82f6" viewBox="0 0 256 256">
                            <path d="M224,48H32A16,16,0,0,0,16,64V192a16,16,0,0,0,16,16H224a16,16,0,0,0,16-16V64A16,16,0,0,0,224,48Zm0,16V80H32V64Zm0,128H32V96H224v96Zm-16-40a8,8,0,0,1-8,8H168a8,8,0,0,1,0-16h32A8,8,0,0,1,208,152Zm-64,0a8,8,0,0,1-8,8H120a8,8,0,0,1,0-16h16A8,8,0,0,1,144,152Z"/>
                        </svg>
                        <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#111827' }}>Tarjeta</span>
                    </button>

                    <button onClick={() => { setPaymentMethod('transferencia'); setCheckoutStep('transferencia'); }}
                        style={{ padding: '1.25rem 0.5rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.12)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#8b5cf6" viewBox="0 0 256 256">
                            <path d="M224,48H32A16,16,0,0,0,16,64V192a16,16,0,0,0,16,16H224a16,16,0,0,0,16-16V64A16,16,0,0,0,224,48Zm-96,88H64a8,8,0,0,1,0-16h64a8,8,0,0,1,0,16Zm80,0H184a8,8,0,0,1,0-16h24a8,8,0,0,1,0,16Z"/>
                        </svg>
                        <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#111827' }}>Transferencia</span>
                    </button>
                    
                    <button onClick={() => { setPaymentMethod('dividido'); setCheckoutStep('dividido'); }}
                        style={{ padding: '1.25rem 0.5rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.12)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#f59e0b" viewBox="0 0 256 256">
                            <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216ZM168,88H136V56a8,8,0,0,0-16,0V88H88a8,8,0,0,0,0,16h32v32H88a8,8,0,0,0,0,16h32v32a8,8,0,0,0,16,0V152h32a8,8,0,0,0,0-16H136V104h32a8,8,0,0,0,0-16Z"/>
                        </svg>
                        <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#111827' }}>Pago Mixto</span>
                    </button>
                </div>
            </div>
        );
    };
    // Modal para agregar nota
    const renderNoteModal = () => (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: screenWidth <= 1366 ? '0.5rem' : '1rem'
        }}>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                width: '100%',
                maxWidth: screenWidth <= 1366 ? '95%' : '500px',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                padding: screenWidth <= 1366 ? '1rem' : '1.5rem'
            }}>
                <div style={{
                    marginBottom: '1rem',
                    borderBottom: '2px solid #e5e7eb',
                    paddingBottom: '0.75rem'
                }}>
                    <h3 style={{
                        fontSize: screenWidth <= 1366 ? '1.125rem' : '1.25rem',
                        fontWeight: '700',
                        color: '#111827',
                        margin: 0
                    }}>
                        Agregar Nota Especial
                    </h3>
                    <p style={{
                        fontSize: screenWidth <= 1366 ? '0.75rem' : '0.875rem',
                        color: '#6b7280',
                        marginTop: '0.25rem'
                    }}>
                        Escribe las especificaciones del producto (aparecerá entre paréntesis en el ticket)
                    </p>
                </div>

                <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Ej: Sin cebolla, extra queso, bien cocido, etc."
                    style={{
                        width: '100%',
                        height: '120px',
                        padding: '0.75rem',
                        border: '2px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '1rem',
                        fontFamily: 'inherit',
                        resize: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '1.5rem'
                    }}
                    maxLength={100}
                />

                <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        style={{
                            padding: screenWidth <= 1366 ? '0.5rem 1rem' : '0.75rem 1.5rem',
                            backgroundColor: '#ffffff',
                            border: '2px solid #d1d5db',
                            borderRadius: '8px',
                            color: '#374151',
                            fontWeight: '600',
                            fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minHeight: TOUCH_MIN_SIZE
                        }}
                        onClick={cancelNote}
                    >
                        Cancelar
                    </button>
                    <button
                        style={{
                            padding: screenWidth <= 1366 ? '0.5rem 1rem' : '0.75rem 1.5rem',
                            backgroundColor: 'var(--primary-color)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontWeight: '600',
                            fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minHeight: TOUCH_MIN_SIZE
                        }}
                        onClick={saveNote}
                    >
                        Guardar Nota
                    </button>
                </div>
            </div>
        </div>
    );

    // Renderizar vista con botones abajo (para pantallas <= 1366px - menos de 16 pulgadas)
    const renderCompactView = () => (
        <div style={{
            height: '100dvh', // Usar dvh para móviles/tablets
            maxHeight: '-webkit-fill-available', // Fallback iOS
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--sidebar-bg)',
            overflow: 'hidden',
            position: 'fixed', // Fijar viewport
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: '#ffffff',
                borderBottom: '2px solid #e5e7eb',
                padding: '0.75rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                flexShrink: 0,
                zIndex: 10
            }}>
                <h1 style={{
                    fontSize: screenWidth <= 768 ? '1.25rem' : '1.5rem',
                    fontWeight: '700',
                    color: '#111827',
                    margin: 0,
                    textAlign: 'center'
                }}>
                    Punto de Venta
                </h1>
            </div>

            {/* Contenido principal - Alterna entre productos y orden */}
            {!showOrderDetails ? (
                // Vista de productos
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0, // Clave para que el scroll interno funcione en flex
                    overflow: 'hidden'
                }}>
                    {/* Filtros */}
                    <div style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0
                    }}>
                        <div style={{
                            display: 'flex',
                            overflowX: 'auto',
                            gap: '0.5rem',
                            paddingBottom: '0.25rem'
                        }}>
                            <button
                                style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '6px',
                                    border: selectedCategory === 'all' ? 'none' : '2px solid #d1d5db',
                                    backgroundColor: selectedCategory === 'all' ? 'var(--primary-color)' : '#ffffff',
                                    color: selectedCategory === 'all' ? '#ffffff' : '#374151',
                                    fontWeight: '600',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={() => setSelectedCategory('all')}
                            >
                                Todos
                            </button>
                            {promotions.length > 0 && (
                                <button
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === 'promotions' ? 'none' : '2px solid #fbbf24',
                                        backgroundColor: selectedCategory === 'promotions' ? '#f59e0b' : '#fffbeb',
                                        color: selectedCategory === 'promotions' ? '#ffffff' : '#b45309',
                                        fontWeight: '700',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory('promotions')}
                                >
                                    ⭐ Promociones
                                </button>
                            )}
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === cat.id ? 'none' : '2px solid #d1d5db',
                                        backgroundColor: selectedCategory === cat.id ? 'var(--primary-color)' : '#ffffff',
                                        color: selectedCategory === cat.id ? '#ffffff' : '#374151',
                                        fontWeight: '600',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory(cat.id)}
                                >
                                    {cat.name.length > 12 ? cat.name.substring(0, 10) + '...' : cat.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Grid Productos */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.75rem',
                        backgroundColor: 'var(--sidebar-bg)'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: screenWidth <= 768 ?
                                'repeat(auto-fill, minmax(140px, 1fr))' :
                                'repeat(auto-fill, minmax(160px, 1fr))',
                            gap: '0.75rem'
                        }}>
                            {(selectedCategory === 'promotions' ? promotions : filteredProducts).map(item => (
                                <div
                                    key={item.id}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                    }}
                                    onClick={() => selectedCategory === 'promotions' ? handleAddPromo(item) : handleProductClick(item)}
                                    onMouseEnter={screenWidth > 768 ? (e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                        e.currentTarget.style.borderColor = selectedCategory === 'promotions' ? '#f59e0b' : 'var(--primary-color)';
                                    } : undefined}
                                    onMouseLeave={screenWidth > 768 ? (e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                    } : undefined}
                                >
                                    <div style={{
                                        height: screenWidth <= 768 ? '80px' : '100px',
                                        backgroundColor: '#eff6ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.5rem'
                                    }}>
                                        <div style={{
                                            width: screenWidth <= 768 ? '48px' : '64px',
                                            height: screenWidth <= 768 ? '48px' : '64px',
                                            borderRadius: '50%',
                                            backgroundColor: 'var(--primary-color)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <span style={{
                                                color: '#ffffff',
                                                fontSize: screenWidth <= 768 ? '1.5rem' : '2rem',
                                                fontWeight: '700',
                                                textTransform: 'uppercase'
                                            }}>
                                                {item.name ? item.name.charAt(0) : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ padding: '0.5rem' }}>
                                        <h3 style={{
                                            fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                            fontWeight: '600',
                                            color: selectedCategory === 'promotions' ? '#b45309' : 'var(--primary-color)',
                                            marginBottom: '0.25rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {item.name}
                                        </h3>
                                        {item.description && (
                                            <p style={{
                                                fontSize: '0.75rem',
                                                color: '#6b7280',
                                                marginBottom: '0.25rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}>
                                                {item.description}
                                            </p>
                                        )}
                                        <p style={{
                                            fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                                            fontWeight: '700',
                                            color: selectedCategory === 'promotions' ? '#b45309' : 'var(--primary-color)',
                                            margin: 0
                                        }}>
                                            ${item.price}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                // Vista de orden
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                    {/* Header de orden */}
                    <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0,
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <h3 style={{
                            fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                            fontWeight: '700',
                            color: '#111827',
                            margin: 0
                        }}>
                            Orden Actual ({cart.length})
                        </h3>
                        <button
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: 'var(--primary-color)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                minHeight: TOUCH_MIN_SIZE
                            }}
                            onClick={() => setShowOrderDetails(false)}
                        >
                            ← Productos
                        </button>
                    </div>

                    {/* Contenido del carrito */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.75rem',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {cart.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '3rem 1rem',
                                color: '#9ca3af',
                                fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem'
                            }}>
                                <p style={{ margin: 0 }}>No hay productos en el carrito</p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                flex: 1
                            }}>
                                {cart.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            padding: '0.75rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem'
                                        }}
                                    >
                                        {/* Información del producto */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{
                                                    fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                                    fontWeight: '600',
                                                    color: 'var(--primary-color)',
                                                    marginBottom: '0.25rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {item.name}
                                                </h4>
                                                <p style={{
                                                    fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                    color: '#6b7280',
                                                    margin: 0
                                                }}>
                                                    {item.discount_percentage > 0 ? (
                                                        <>
                                                            <span style={{ textDecoration: 'line-through', marginRight: '0.25rem', color: '#9ca3af' }}>
                                                                {formatCurrency(item.price)}
                                                            </span>
                                                            <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                                                                {formatCurrency(item.price * (1 - (parseFloat(item.discount_percentage) || 0) / 100))} c/u
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>{formatCurrency(item.price)} c/u</>
                                                    )}
                                                </p>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <button
                                                        style={{
                                                            width: screenWidth <= 768 ? '36px' : '40px',
                                                            height: screenWidth <= 768 ? '36px' : '40px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#6b7280',
                                                            fontSize: screenWidth <= 768 ? '1rem' : '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, -1)}
                                                    >
                                                        −
                                                    </button>
                                                    <span style={{
                                                        width: screenWidth <= 768 ? '30px' : '34px',
                                                        textAlign: 'center',
                                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                                        fontWeight: '600',
                                                        color: 'var(--primary-color)'
                                                    }}>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        style={{
                                                            width: screenWidth <= 768 ? '36px' : '40px',
                                                            height: screenWidth <= 768 ? '36px' : '40px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#6b7280',
                                                            fontSize: screenWidth <= 768 ? '1rem' : '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, 1)}
                                                    >
                                                        +
                                                    </button>
                                                </div>

                                                <button
                                                    style={{
                                                        width: screenWidth <= 768 ? '36px' : '40px',
                                                        height: screenWidth <= 768 ? '36px' : '40px',
                                                        backgroundColor: '#fee2e2',
                                                        border: '2px solid #fecaca',
                                                        borderRadius: '6px',
                                                        color: '#dc2626',
                                                        fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    onClick={() => removeFromCart(item)}
                                                    title="Eliminar producto"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>

                                        {/* Nota y descuento del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingTop: '0.5rem',
                                            borderTop: '1px dashed #e5e7eb'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                {item.note ? (
                                                    <div style={{
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        color: '#6b7280',
                                                        fontStyle: 'italic',
                                                        backgroundColor: 'var(--sidebar-bg)',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        <strong>Nota:</strong> {item.note}
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        color: '#9ca3af',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Sin notas (mt: {item.max_toppings})
                                                    </span>
                                                )}
                                            </div>

                                            {/* Variables seleccionadas movidas al nombre */}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <span style={{ padding: '0.25rem', backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem' }}>%</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={item.discount_percentage || ''}
                                                        onChange={(e) => updateItemDiscount(item, e.target.value)}
                                                        placeholder="0"
                                                        style={{
                                                            width: '32px',
                                                            border: 'none',
                                                            padding: '0.25rem',
                                                            textAlign: 'center',
                                                            fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                </div>

                                                <button
                                                    style={{
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: item.note ? '#fef3c7' : 'var(--sidebar-bg)',
                                                        border: `1px solid ${item.note ? '#fbbf24' : '#d1d5db'}`,
                                                        borderRadius: '4px',
                                                        color: item.note ? '#92400e' : '#374151',
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        marginLeft: '0.25rem',
                                                        whiteSpace: 'nowrap',
                                                        minHeight: TOUCH_MIN_SIZE
                                                    }}
                                                    onClick={() => handleAddNote(item)}
                                                    title={item.note ? "Editar nota" : "Agregar nota"}
                                                >
                                                    {item.note ? '📝 Editar' : '✏️ Nota'}
                                                </button>
                                                
                                                    <button
                                                        onClick={() => {
                                                            setActiveToppingsItemIndex(index);
                                                            setShowToppingsModal(true);
                                                        }}
                                                        style={{
                                                            padding: '0.25rem 0.5rem',
                                                            backgroundColor: item.selectedToppings?.length > 0 ? 'var(--primary-color)' : 'var(--sidebar-bg)',
                                                            border: `1px solid ${item.selectedToppings?.length > 0 ? 'var(--primary-color)' : '#d1d5db'}`,
                                                            borderRadius: '4px',
                                                            color: item.selectedToppings?.length > 0 ? '#fff' : '#374151',
                                                            fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                            fontWeight: '500',
                                                            cursor: 'pointer',
                                                            marginLeft: '0.25rem',
                                                            whiteSpace: 'nowrap',
                                                            minHeight: TOUCH_MIN_SIZE
                                                        }}
                                                        title="Agregar toppings"
                                                    >
                                                        ➕ Toppings {item.selectedToppings?.length > 0 ? `(${item.selectedToppings.length}/${item.max_toppings})` : ''}
                                                    </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Totales y Botones */}
                        {cart.length > 0 && (
                            <div style={{
                                marginTop: 'auto',
                                paddingTop: '1rem',
                                borderTop: '2px solid #e5e7eb'
                            }}>
                                {/* Totales */}
                                <div style={{
                                    paddingBottom: '1rem',
                                    marginBottom: '0.75rem'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.5rem',
                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                        color: '#6b7280'
                                    }}>
                                        <span>Subtotal</span>
                                        <span style={{ fontWeight: '600' }}>{formatCurrency(calculateSubtotal)}</span>
                                    </div>

                                    {appliedDiscount && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '0.5rem',
                                            fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                            color: '#dc2626',
                                            fontWeight: '600'
                                        }}>
                                            <span>Descuento</span>
                                            <span>- {formatCurrency(calculateDiscountAmount)}</span>
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        paddingTop: '0.75rem',
                                        borderTop: '2px solid #e5e7eb',
                                        fontSize: screenWidth <= 768 ? '1.25rem' : '1.5rem',
                                        fontWeight: '700',
                                        color: '#111827'
                                    }}>
                                        <span>Total</span>
                                        <span style={{ color: 'var(--primary-color)' }}>{formatCurrency(calculateTotal)}</span>
                                    </div>
                                </div>

                                {/* Botones principales */}
                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        backgroundColor: processingOrder ? '#d1d5db' : 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                                        fontWeight: '700',
                                        cursor: processingOrder ? 'not-allowed' : 'pointer',
                                        marginBottom: '0.5rem',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={openOrderConfirmationModal}
                                    disabled={processingOrder}
                                >
                                    {processingOrder ? 'Procesando...' : 'Revisar y Pagar'}
                                </button>

                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={handleOpenCashDrawer}
                                >
                                    🔓 Abrir Caja
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Barra inferior con botones */}
            <div style={{
                backgroundColor: '#ffffff',
                borderTop: '2px solid #e5e7eb',
                padding: '0.5rem',
                display: 'flex',
                gap: '0.5rem',
                flexShrink: 0
            }}>
                <button
                    style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: showOrderDetails ? '#e5e7eb' : 'var(--primary-color)',
                        border: 'none',
                        borderRadius: '8px',
                        color: showOrderDetails ? '#374151' : '#ffffff',
                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minHeight: TOUCH_MIN_SIZE
                    }}
                    onClick={() => setShowOrderDetails(false)}
                >
                    Productos
                </button>
                <button
                    style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: !showOrderDetails ? '#e5e7eb' : 'var(--primary-color)',
                        border: 'none',
                        borderRadius: '8px',
                        color: !showOrderDetails ? '#374151' : '#ffffff',
                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minHeight: TOUCH_MIN_SIZE,
                        position: 'relative'
                    }}
                    onClick={() => setShowOrderDetails(true)}
                >
                    Orden {cart.length > 0 && (
                        <span style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {cart.length}
                        </span>
                    )}
                </button>
            </div>
        </div>
    );

    // Renderizar vista de escritorio dividida (para pantallas > 1366px - más de 16 pulgadas)
    const renderDesktopView = () => (
        <div style={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--sidebar-bg)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: '#ffffff',
                borderBottom: '2px solid #e5e7eb',
                padding: '1rem 1.5rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
                <h1 style={{
                    fontSize: '1.75rem',
                    fontWeight: '700',
                    color: '#111827',
                    margin: 0,
                    letterSpacing: '-0.025em'
                }}>
                    Punto de Venta
                </h1>
            </div>

            <div style={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
                flexDirection: 'row'
            }}>
                {/* Panel Izquierdo: Catálogo */}
                <div style={{
                    flex: '1 1 60%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#ffffff',
                    borderRight: '2px solid #e5e7eb',
                    minHeight: 'auto',
                    minWidth: 0,
                    maxWidth: '100%'
                }}>
                    {/* Contenido Principal */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        {selectedCategory === 'all' ? (
                            // VISTA DE CATEGORÍAS (RAÍZ)
                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '1.5rem',
                                backgroundColor: 'var(--sidebar-bg)'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                    gap: '1.5rem'
                                }}>
                                    {promotions.length > 0 && (
                                        <div
                                            onClick={() => setSelectedCategory('promotions')}
                                            style={{
                                                backgroundColor: '#fffbeb',
                                                border: '3px solid #f59e0b',
                                                borderRadius: '16px',
                                                padding: '1.25rem',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                minHeight: '120px'
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 15px rgba(245, 158, 11, 0.2)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)'; }}
                                        >
                                            <h2 style={{ color: '#b45309', margin: 0, fontSize: '1.3rem', fontWeight: '800' }}>⭐ PROMOCIONES</h2>
                                            <span style={{ color: '#d97706', fontSize: '1rem', marginTop: '0.5rem' }}>{promotions.length} promociones</span>
                                        </div>
                                    )}
                                    
                                    {categories.filter(c => !c.parent).map(cat => {
                                        const bgColor = cat.background_color || '#ffffff';
                                        const borderColor = cat.color || '#e5e7eb';
                                        return (
                                            <div
                                                key={cat.id}
                                                onClick={() => setSelectedCategory(cat.id)}
                                                style={{
                                                    backgroundColor: bgColor,
                                                    border: `3px solid ${borderColor}`,
                                                    borderRadius: '16px',
                                                    padding: '1.25rem',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    minHeight: '120px'
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 10px 15px ${borderColor}40`; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)'; }}
                                            >
                                                <h2 style={{ color: borderColor, margin: 0, fontSize: '1.3rem', fontWeight: '800', textTransform: 'uppercase' }}>
                                                    {cat.name}
                                                </h2>
                                                <span style={{ color: '#6b7280', fontSize: '1rem', marginTop: '0.5rem' }}>
                                                    {products.filter(p => getAllCategoryDescendants(cat.id, categories).includes(p.category) && p.is_active && p.is_available).length} productos
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            // VISTA DE PRODUCTOS (DRILL-DOWN)
                            <>
                                {/* Header de la categoría con botón volver */}
                                <div style={{
                                    padding: '1rem 1.5rem',
                                    borderBottom: '1px solid #e5e7eb',
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem'
                                }}>
                                    <button
                                        onClick={() => {
                                            const currentCat = categories.find(c => c.id === selectedCategory);
                                            if (currentCat?.parent) {
                                                setSelectedCategory(currentCat.parent);
                                            } else {
                                                setSelectedCategory('all');
                                            }
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.5rem',
                                            color: '#6b7280',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <i className="bi bi-arrow-left"></i>
                                    </button>
                                    <h2 style={{ margin: 0, color: '#111827', fontSize: '1.5rem', fontWeight: '700' }}>
                                        {selectedCategory === 'promotions' ? 'Promociones' : categories.find(c => c.id === selectedCategory)?.name}
                                    </h2>
                                </div>
                                
                                {/* Grid de Productos */}
                                <div style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    padding: '1.5rem',
                                    backgroundColor: 'var(--sidebar-bg)'
                                }}>
                                    {/* Grid de Subcategorías */}
                                    {categories.filter(c => c.parent === selectedCategory).length > 0 && (
                                        <>
                                            <h3 style={{
                                                fontSize: '1.25rem',
                                                fontWeight: '700',
                                                color: '#374151',
                                                marginBottom: '1rem',
                                                borderBottom: '2px solid #e5e7eb',
                                                paddingBottom: '0.5rem'
                                            }}>
                                                Categorías
                                            </h3>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                                gap: '1.5rem',
                                                marginBottom: '2rem'
                                            }}>
                                            {categories.filter(c => c.parent === selectedCategory).map(cat => {
                                                const bgColor = cat.background_color || '#ffffff';
                                                const borderColor = cat.color || '#e5e7eb';
                                                return (
                                                    <div
                                                        key={cat.id}
                                                        onClick={() => setSelectedCategory(cat.id)}
                                                        style={{
                                                            backgroundColor: bgColor,
                                                            border: `3px solid ${borderColor}`,
                                                            borderRadius: '16px',
                                                            padding: '1.25rem',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                                            minHeight: '120px'
                                                        }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 10px 15px ${borderColor}40`; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)'; }}
                                                    >
                                                        <h2 style={{ color: borderColor, margin: 0, fontSize: '1.3rem', fontWeight: '800', textTransform: 'uppercase' }}>
                                                            {cat.name}
                                                        </h2>
                                                        <span style={{ color: '#6b7280', fontSize: '1rem', marginTop: '0.5rem' }}>
                                                            {products.filter(p => getAllCategoryDescendants(cat.id, categories).includes(p.category) && p.is_active && p.is_available).length} productos
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        </>
                                    )}

                                    {/* Grid de Productos Directos */}
                                    {(selectedCategory === 'promotions' || filteredProducts.length > 0) && (
                                        <>
                                            {selectedCategory !== 'promotions' && (
                                                <h3 style={{
                                                    fontSize: '1.25rem',
                                                    fontWeight: '700',
                                                    color: '#374151',
                                                    marginBottom: '1rem',
                                                    borderBottom: '2px solid #e5e7eb',
                                                    paddingBottom: '0.5rem'
                                                }}>
                                                    Productos
                                                </h3>
                                            )}
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                                gap: '1rem'
                                            }}>
                                        {(selectedCategory === 'promotions' ? promotions : filteredProducts).map(item => (
                                            <div
                                                key={item.id}
                                                style={{
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '10px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    border: '1px solid #e5e7eb',
                                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                                }}
                                                onClick={() => selectedCategory === 'promotions' ? handleAddPromo(item) : handleProductClick(item)}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                                    e.currentTarget.style.borderColor = selectedCategory === 'promotions' ? '#f59e0b' : 'var(--primary-color)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                                }}
                                            >
                                                <div style={{
                                                    height: '140px',
                                                    backgroundColor: '#eff6ff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '0.75rem',
                                                    borderBottom: '1px solid #bfdbfe'
                                                }}>
                                                    <div style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'var(--primary-color)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <span style={{
                                                            color: '#ffffff',
                                                            fontSize: '3rem',
                                                            fontWeight: '800',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            {item.name ? item.name.charAt(0) : ''}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div style={{ padding: '0.875rem' }}>
                                                    <h3 style={{
                                                        fontSize: '0.9375rem',
                                                        fontWeight: '600',
                                                        color: selectedCategory === 'promotions' ? '#b45309' : 'var(--primary-color)',
                                                        marginBottom: '0.375rem',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {item.name}
                                                    </h3>
                                                    {item.description && (
                                                        <p style={{
                                                            fontSize: '0.75rem',
                                                            color: '#6b7280',
                                                            marginBottom: '0.375rem',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical'
                                                        }}>
                                                            {item.description}
                                                        </p>
                                                    )}
                                                    <p style={{
                                                        fontSize: '1.125rem',
                                                        fontWeight: '700',
                                                        color: selectedCategory === 'promotions' ? '#b45309' : 'var(--primary-color)',
                                                        margin: 0
                                                    }}>
                                                        ${item.price}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Panel Derecho: Orden Actual */}
                <div style={{
                    flex: screenWidth <= 1024 ? '0 0 320px' : '0 0 400px',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.05)',
                    flexShrink: 0
                }}>
                    {/* Header de Orden Actual */}
                    <div style={{
                        padding: '1rem 1.5rem',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0,
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <h3 style={{
                            fontSize: '1.125rem',
                            fontWeight: '700',
                            color: '#111827',
                            margin: 0
                        }}>
                            Orden Actual
                        </h3>
                    </div>

                    {/* Contenido del Carrito */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '1.5rem',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {cart.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '3rem 1rem',
                                color: '#9ca3af',
                                fontSize: '0.875rem'
                            }}>
                                <p style={{ margin: 0 }}>No hay productos en el carrito</p>
                                <p style={{
                                    margin: '0.5rem 0 0 0',
                                    fontSize: '0.8125rem'
                                }}>
                                    Selecciona productos para comenzar
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.75rem',
                                flex: 1
                            }}>
                                {cart.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '10px',
                                            padding: '1rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.75rem'
                                        }}
                                    >
                                        {/* Información del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '0.75rem'
                                        }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{
                                                    fontSize: '0.9375rem',
                                                    fontWeight: '600',
                                                    color: 'var(--primary-color)',
                                                    marginBottom: '0.375rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {item.name}
                                                </h4>
                                                <p style={{
                                                    fontSize: '0.8125rem',
                                                    color: '#6b7280',
                                                    margin: 0
                                                }}>
                                                    {item.discount_percentage > 0 ? (
                                                        <>
                                                            <span style={{ textDecoration: 'line-through', marginRight: '0.25rem', color: '#9ca3af' }}>
                                                                {formatCurrency(item.price)}
                                                            </span>
                                                            <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                                                                {formatCurrency(item.price * (1 - (parseFloat(item.discount_percentage) || 0) / 100))} c/u
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>{formatCurrency(item.price)} c/u</>
                                                    )}
                                                </p>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <button
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#6b7280',
                                                            fontSize: '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, -1)}
                                                    >
                                                        −
                                                    </button>
                                                    <span style={{
                                                        width: '36px',
                                                        textAlign: 'center',
                                                        fontSize: '0.9375rem',
                                                        fontWeight: '600',
                                                        color: 'var(--primary-color)'
                                                    }}>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#6b7280',
                                                            fontSize: '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, 1)}
                                                    >
                                                        +
                                                    </button>
                                                </div>

                                                <button
                                                    style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        backgroundColor: '#fee2e2',
                                                        border: '2px solid #fecaca',
                                                        borderRadius: '8px',
                                                        color: '#dc2626',
                                                        fontSize: '1.125rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    onClick={() => removeFromCart(item)}
                                                    title="Eliminar producto"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>

                                        {/* Nota y descuento del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingTop: '0.75rem',
                                            borderTop: '1px dashed #e5e7eb'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                {item.note ? (
                                                    <div style={{
                                                        fontSize: '0.8125rem',
                                                        color: '#6b7280',
                                                        fontStyle: 'italic',
                                                        backgroundColor: 'var(--sidebar-bg)',
                                                        padding: '0.375rem 0.75rem',
                                                        borderRadius: '4px',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        <strong>Nota:</strong> {item.note}
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        fontSize: '0.8125rem',
                                                        color: '#9ca3af',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Sin notas especiales
                                                    </span>
                                                )}
                                            </div>

                                            {/* Variables seleccionadas movidas al nombre */}
                                            
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <span style={{ padding: '0.375rem', backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: '0.8125rem' }}>%</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={item.discount_percentage || ''}
                                                        onChange={(e) => updateItemDiscount(item, e.target.value)}
                                                        placeholder="0"
                                                        style={{
                                                            width: '40px',
                                                            border: 'none',
                                                            padding: '0.375rem',
                                                            textAlign: 'center',
                                                            fontSize: '0.8125rem',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                </div>

                                                <button
                                                    style={{
                                                        padding: '0.375rem 0.75rem',
                                                        backgroundColor: item.note ? '#fef3c7' : 'var(--sidebar-bg)',
                                                        border: `1px solid ${item.note ? '#fbbf24' : '#d1d5db'}`,
                                                        borderRadius: '4px',
                                                        color: item.note ? '#92400e' : '#374151',
                                                        fontSize: '0.8125rem',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap',
                                                        minHeight: TOUCH_MIN_SIZE,
                                                        minWidth: '60px'
                                                    }}
                                                    onClick={() => handleAddNote(item)}
                                                    title={item.note ? "Editar nota" : "Agregar nota"}
                                                >
                                                    {item.note ? '📝 Editar' : '✏️ Nota'}
                                                </button>

                                                {item.max_toppings > 0 && (
                                                    <button
                                                        onClick={() => {
                                                            setActiveToppingsItemIndex(index);
                                                            setShowToppingsModal(true);
                                                        }}
                                                        style={{
                                                            padding: '0.375rem 0.75rem',
                                                            backgroundColor: item.selectedToppings?.length > 0 ? 'var(--primary-color)' : 'var(--sidebar-bg)',
                                                            border: `1px solid ${item.selectedToppings?.length > 0 ? 'var(--primary-color)' : '#d1d5db'}`,
                                                            borderRadius: '4px',
                                                            color: item.selectedToppings?.length > 0 ? '#fff' : '#374151',
                                                            fontSize: '0.8125rem',
                                                            fontWeight: '500',
                                                            cursor: 'pointer',
                                                            whiteSpace: 'nowrap',
                                                            minHeight: TOUCH_MIN_SIZE
                                                        }}
                                                        title="Agregar toppings"
                                                    >
                                                        ➕ Toppings {item.selectedToppings?.length > 0 ? `(${item.selectedToppings.length}/${item.max_toppings})` : ''}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Totales y Botones - SOLO se muestra cuando hay productos */}
                        {cart.length > 0 && (
                            <div style={{
                                marginTop: 'auto',
                                paddingTop: '1.5rem',
                                borderTop: '2px solid #e5e7eb'
                            }}>
                                {/* Totales */}
                                <div style={{
                                    paddingBottom: '1rem',
                                    marginBottom: '1rem'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.5rem',
                                        fontSize: '0.9375rem',
                                        color: '#6b7280'
                                    }}>
                                        <span>Subtotal</span>
                                        <span style={{ fontWeight: '600' }}>{formatCurrency(calculateSubtotal)}</span>
                                    </div>

                                    {appliedDiscount && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '0.5rem',
                                            fontSize: '0.9375rem',
                                            color: '#dc2626',
                                            fontWeight: '600'
                                        }}>
                                            <span>Descuento</span>
                                            <span>- {formatCurrency(calculateDiscountAmount)}</span>
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        paddingTop: '1rem',
                                        borderTop: '2px solid #e5e7eb',
                                        fontSize: '1.5rem',
                                        fontWeight: '700',
                                        color: '#111827'
                                    }}>
                                        <span>Total</span>
                                        <span style={{ color: 'var(--primary-color)' }}>{formatCurrency(calculateTotal)}</span>
                                    </div>
                                </div>

                                {/* Botón Principal */}
                                <button
                                    style={{
                                        width: '100%',
                                        padding: '1rem',
                                        backgroundColor: processingOrder ? '#d1d5db' : 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: '#ffffff',
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        cursor: processingOrder ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        boxShadow: processingOrder ? 'none' : '0 4px 12px rgba(100, 16, 14, 0.3)',
                                        marginBottom: '0.75rem',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={openOrderConfirmationModal}
                                    disabled={processingOrder}
                                >
                                    {processingOrder ? 'Procesando pedido...' : 'Revisar y Pagar'}
                                </button>

                                {/* 🔓 Botón Abrir Caja */}
                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontSize: '0.9375rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={handleOpenCashDrawer}
                                >
                                    🔓 Abrir Caja Registradora
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading || checkingShift) return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.125rem',
            color: '#6b7280'
        }}>
            Cargando sistema de punto de venta...
        </div>
    );

    if (!currentShift || !currentShift.id || currentShift.status !== 'open') {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--sidebar-bg)',
                padding: '2rem',
                textAlign: 'center'
            }}>
                <i className="bi bi-lock-fill" style={{ fontSize: '5rem', color: '#dc2626', marginBottom: '1rem' }}></i>
                <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>Caja Cerrada</h2>
                <p style={{ fontSize: '1.25rem', color: '#4b5563', marginBottom: '2.5rem', maxWidth: '600px', lineHeight: '1.5' }}>
                    Debes abrir la caja registradora antes de poder acceder al Punto de Venta y realizar ventas.
                </p>
                <button
                    onClick={() => navigate('/heladeria/shift')}
                    style={{
                        padding: '1rem 2.5rem',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    Ir a Abrir Caja
                </button>
            </div>
        );
    }

    // =====================================
    // 11. ESTRUCTURA PRINCIPAL CON RESPONSIVIDAD
    // =====================================

    // 576px es el breakpoint para móviles, de modo que las tablets usen la vista de escritorio
    const isSmallScreen = screenWidth <= 576;

    return (
        <>
            {/* Vista responsiva basada en el tamaño de pantalla */}
            {isSmallScreen ? renderCompactView() : renderDesktopView()}

            {/* Modal para agregar nota */}
            {editingNoteForItem && renderNoteModal()}

            {/* Modal Confirmación de Pago - Diseño compacto */}
            {showReviewModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.55)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '380px',
                        boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
                        overflow: 'hidden'
                    }}>
                        {renderReviewDetails()}
                    </div>
                </div>
            )}

            {/* Modal Crear Cliente (Compartido) */}
            {showCustomerModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: isSmallScreen ? '0.5rem' : '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: isSmallScreen ? '95%' : '500px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            padding: isSmallScreen ? '1rem' : '1.5rem',
                            borderBottom: '2px solid #e5e7eb',
                            backgroundColor: 'var(--sidebar-bg)'
                        }}>
                            <h3 style={{
                                fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                                fontWeight: '700',
                                color: '#111827',
                                margin: 0
                            }}>
                                Nuevo Cliente
                            </h3>
                        </div>

                        <form onSubmit={handleCreateCustomer} style={{ padding: isSmallScreen ? '1rem' : '1.5rem' }}>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Cédula / RUC (Identificación)
                                </label>
                                <input
                                    type="text"
                                    name="cedula"
                                    value={newCustomer.cedula}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        transition: 'all 0.2s',
                                        boxSizing: 'border-box',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Correo Electrónico
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={newCustomer.email}
                                    onChange={handleInputChange}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        transition: 'all 0.2s',
                                        boxSizing: 'border-box',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                />
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '0.75rem',
                                marginBottom: '1rem'
                            }}>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                        fontWeight: '600',
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Nombre
                                    </label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={newCustomer.first_name}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                            border: '2px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                            transition: 'all 0.2s',
                                            boxSizing: 'border-box',
                                            minHeight: TOUCH_MIN_SIZE
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                        fontWeight: '600',
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Apellido
                                    </label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={newCustomer.last_name}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                            border: '2px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                            transition: 'all 0.2s',
                                            boxSizing: 'border-box',
                                            minHeight: TOUCH_MIN_SIZE
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Teléfono
                                </label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={newCustomer.phone}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        transition: 'all 0.2s',
                                        boxSizing: 'border-box',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: isSmallScreen ? '0.875rem' : '0.875rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Ciudad
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    value={newCustomer.city}
                                    onChange={handleInputChange}
                                    style={{
                                        width: '100%',
                                        padding: isSmallScreen ? '0.5rem' : '0.75rem',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        transition: 'all 0.2s',
                                        boxSizing: 'border-box',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                />
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: '0.75rem',
                                justifyContent: 'flex-end',
                                borderTop: '2px solid #e5e7eb',
                                paddingTop: '1rem'
                            }}>
                                <button
                                    type="button"
                                    style={{
                                        padding: isSmallScreen ? '0.5rem 1rem' : '0.75rem 1.5rem',
                                        backgroundColor: '#ffffff',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        color: '#374151',
                                        fontWeight: '600',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setShowCustomerModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        padding: isSmallScreen ? '0.5rem 1rem' : '0.75rem 1.5rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontWeight: '600',
                                        fontSize: isSmallScreen ? '0.875rem' : '0.9375rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                >
                                    Guardar Cliente
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* Modal de Toppings */}
            {showToppingsModal && activeToppingsItemIndex !== null && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '400px',
                        padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                        maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: 0, color: '#111827', fontSize: '1.25rem' }}>Seleccionar Toppings</h3>
                            <button 
                                onClick={() => setShowToppingsModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#9ca3af', cursor: 'pointer' }}
                            >×</button>
                        </div>
                        
                        <p style={{ color: '#4b5563', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            Puedes seleccionar hasta {cart[activeToppingsItemIndex].max_toppings} toppings.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {availableToppings.map(topping => {
                                const activeItem = cart[activeToppingsItemIndex];
                                const count = activeItem.selectedToppings?.filter(t => t === topping.name).length || 0;
                                const canSelectMore = (activeItem.selectedToppings?.length || 0) < activeItem.max_toppings;
                                
                                return (
                                    <div 
                                        key={topping.id}
                                        style={{
                                            padding: '0.75rem 1rem',
                                            borderRadius: '8px',
                                            border: `1px solid #e5e7eb`,
                                            backgroundColor: count > 0 ? '#fef2f2' : '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <span style={{ fontWeight: count > 0 ? 'bold' : 'normal', color: '#111827' }}>
                                            {topping.name}
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <button
                                                onClick={() => {
                                                    if (count > 0) {
                                                        const newCart = [...cart];
                                                        const currentToppings = newCart[activeToppingsItemIndex].selectedToppings || [];
                                                        const indexToRemove = currentToppings.lastIndexOf(topping.name);
                                                        if (indexToRemove > -1) {
                                                            currentToppings.splice(indexToRemove, 1);
                                                        }
                                                        newCart[activeToppingsItemIndex].selectedToppings = currentToppings;
                                                        setCart(newCart);
                                                    }
                                                }}
                                                style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', cursor: count > 0 ? 'pointer' : 'not-allowed', opacity: count > 0 ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                                            >-</button>
                                            <span style={{ minWidth: '1.5rem', textAlign: 'center', fontWeight: '600' }}>{count}</span>
                                            <button
                                                onClick={() => {
                                                    if (canSelectMore) {
                                                        const newCart = [...cart];
                                                        const currentToppings = newCart[activeToppingsItemIndex].selectedToppings || [];
                                                        currentToppings.push(topping.name);
                                                        newCart[activeToppingsItemIndex].selectedToppings = currentToppings;
                                                        setCart(newCart);
                                                    } else {
                                                        alert(`Límite alcanzado: máximo ${activeItem.max_toppings} toppings.`);
                                                    }
                                                }}
                                                style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', cursor: canSelectMore ? 'pointer' : 'not-allowed', opacity: canSelectMore ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                                            >+</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => setShowToppingsModal(false)}
                                style={{
                                    padding: '0.75rem 1.5rem', backgroundColor: 'var(--primary-color)',
                                    color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                                }}
                            >
                                Listo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Variables */}
            {showVariablesModal && selectedProductForVariables && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#f8fafc', borderRadius: '12px', padding: '2rem',
                        width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                            <div>
                                <h3 style={{ margin: '0 0 0.25rem 0', color: '#111827', fontSize: '1.125rem', fontWeight: '800', textTransform: 'uppercase' }}>
                                    ARMA TU {selectedProductForVariables.product.name}
                                </h3>
                                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.75rem' }}>
                                    Selecciona los ingredientes para personalizar tu producto
                                </p>
                            </div>
                            <button onClick={() => setShowVariablesModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#9ca3af' }}>&times;</button>
                        </div>
                        
                        {[...selectedProductForVariables.product.variables].sort((a, b) => a.name.localeCompare(b.name)).map((variable, index) => {
                            const maxSelections = variable.max_selections || 1;
                            const currentQtyTotal = Object.values(currentVariablesSelection[variable.id] || {}).reduce((a, b) => a + b, 0);
                            
                            return (
                                <div key={variable.id} style={{ marginBottom: '1rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                                        <h4 style={{ margin: '0', color: '#111827', fontSize: '0.9rem', fontWeight: '700' }}>
                                            {index + 1}. {variable.name}
                                        </h4>
                                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                            Máx {maxSelections} (Obligatorio)
                                        </span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.5rem' }}>
                                        {variable.options?.filter(o => o.is_active).map(option => {
                                            const qty = (currentVariablesSelection[variable.id] || {})[option.id] || 0;
                                            
                                            const handleIncrement = (e) => {
                                                e.stopPropagation();
                                                if (currentQtyTotal < maxSelections) {
                                                    setCurrentVariablesSelection(prev => ({
                                                        ...prev,
                                                        [variable.id]: {
                                                            ...(prev[variable.id] || {}),
                                                            [option.id]: qty + 1
                                                        }
                                                    }));
                                                }
                                            };
                                            
                                            return (
                                                <div 
                                                    key={option.id}
                                                    onClick={(e) => {
                                                        if (currentQtyTotal < maxSelections) {
                                                            setCurrentVariablesSelection(prev => ({
                                                                ...prev,
                                                                [variable.id]: {
                                                                    ...(prev[variable.id] || {}),
                                                                    [option.id]: qty + 1
                                                                }
                                                            }));
                                                        } else if (maxSelections === 1 && currentQtyTotal === 1) {
                                                            // For single selection, allow swapping easily
                                                            setCurrentVariablesSelection(prev => ({
                                                                ...prev,
                                                                [variable.id]: {
                                                                    [option.id]: 1
                                                                }
                                                            }));
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '0.5rem 0.75rem',
                                                        border: `1px solid ${qty > 0 ? '#60a5fa' : '#e5e7eb'}`,
                                                        borderRadius: '6px',
                                                        backgroundColor: qty > 0 ? '#eff6ff' : '#ffffff',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                        alignItems: 'flex-start',
                                                        color: '#111827',
                                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                                        cursor: (currentQtyTotal < maxSelections || (maxSelections===1 && qty===0)) ? 'pointer' : 'not-allowed',
                                                        position: 'relative',
                                                        minHeight: '60px'
                                                    }}
                                                >
                                                    {qty > 0 && (
                                                        <div 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (qty > 0) {
                                                                    setCurrentVariablesSelection(prev => ({
                                                                        ...prev,
                                                                        [variable.id]: {
                                                                            ...(prev[variable.id] || {}),
                                                                            [option.id]: qty - 1
                                                                        }
                                                                    }));
                                                                }
                                                            }}
                                                            style={{
                                                                position: 'absolute',
                                                                top: '-8px',
                                                                right: '-8px',
                                                                backgroundColor: '#3b82f6',
                                                                color: '#fff',
                                                                borderRadius: '50%',
                                                                width: '24px',
                                                                height: '24px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Clic para quitar uno"
                                                        >
                                                            {qty}
                                                        </div>
                                                    )}
                                                    <div style={{ fontSize: '0.75rem', fontWeight: '600', lineHeight: '1.2' }}>{option.name}</div>
                                                    {parseFloat(option.extra_price) > 0 && (
                                                        <div style={{ fontSize: '0.65rem', marginTop: '0.125rem', color: '#6b7280' }}>
                                                            (+${parseFloat(option.extra_price).toFixed(2)})
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}

                        <div style={{ 
                            marginTop: '2rem', 
                            paddingTop: '1.5rem', 
                            borderTop: '1px solid #e5e7eb',
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: '1rem' 
                        }}>
                            <button 
                                onClick={() => setShowVariablesModal(false)}
                                style={{
                                    padding: '0.75rem 1.5rem', 
                                    backgroundColor: '#ffffff',
                                    color: '#374151', 
                                    border: '1px solid #d1d5db', 
                                    borderRadius: '6px', 
                                    cursor: 'pointer', 
                                    fontWeight: '600',
                                    fontSize: '0.875rem'
                                }}
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmVariablesSelection}
                                disabled={!selectedProductForVariables.product.variables.every(v => Object.values(currentVariablesSelection[v.id] || {}).reduce((a, b) => a + b, 0) > 0)}
                                style={{
                                    padding: '0.75rem 1.5rem', 
                                    backgroundColor: selectedProductForVariables.product.variables.every(v => Object.values(currentVariablesSelection[v.id] || {}).reduce((a, b) => a + b, 0) > 0) ? '#93c5fd' : '#e5e7eb',
                                    color: selectedProductForVariables.product.variables.every(v => Object.values(currentVariablesSelection[v.id] || {}).reduce((a, b) => a + b, 0) > 0) ? '#ffffff' : '#9ca3af', 
                                    border: 'none', 
                                    borderRadius: '6px', 
                                    cursor: selectedProductForVariables.product.variables.every(v => Object.values(currentVariablesSelection[v.id] || {}).reduce((a, b) => a + b, 0) > 0) ? 'pointer' : 'not-allowed', 
                                    fontWeight: '700',
                                    fontSize: '0.875rem',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Agregar - ${(
                                    parseFloat(selectedProductForVariables.product.price) + 
                                    selectedProductForVariables.product.variables.reduce((total, v) => {
                                        const selection = currentVariablesSelection[v.id] || {};
                                        let varTotal = 0;
                                        v.options.forEach(opt => {
                                            const qty = selection[opt.id] || 0;
                                            varTotal += qty * (parseFloat(opt.extra_price) || 0);
                                        });
                                        return total + varTotal;
                                    }, 0)
                                ).toFixed(2)}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Resultado de la Orden */}
            {orderResultModal.isOpen && (
                <div className="modal-overlay">
                    <div className="modal-content result-modal" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ fontSize: '48px', marginBottom: '15px' }}>
                            {orderResultModal.type === 'success' ? '✅' : orderResultModal.type === 'warning' ? '⚠️' : '❌'}
                        </div>
                        <h2 style={{ marginBottom: '15px', color: orderResultModal.type === 'error' ? '#e74c3c' : '#2c3e50' }}>
                            {orderResultModal.title}
                        </h2>
                        <p style={{ whiteSpace: 'pre-wrap', marginBottom: '25px', color: '#7f8c8d', lineHeight: '1.5' }}>
                            {orderResultModal.message}
                        </p>
                        <button
                            className="btn btn-primary"
                            style={{ padding: '12px 30px', fontSize: '16px', width: '100%' }}
                            onClick={() => {
                                setOrderResultModal({ ...orderResultModal, isOpen: false });
                                if (orderResultModal.type !== 'error') {
                                    setShowReviewModal(false);
                                }
                            }}
                        >
                            {orderResultModal.type === 'error' ? 'Corregir y Reintentar' : 'Aceptar'}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default PuntosVenta;